import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import DashboardGrid from "./Dashboard/DashboardGrid";
import { defaultDashboard } from "../config/defaultDashboard";
import { getEdges } from "../api/edgeApi";
import { getLatestMetrics, getEdgeAlarms, getEdgeAnomalies } from "../api/metricsApi";
import {
  saveDashboardLayout,
  getDashboardLayout,
  getDashboardEdges,
  getDashboardEdgeDevices,
  getDashboardEdgeDeviceTags,
  getDashboardDataLatest,
  getDashboardDataSeries,
  getDashboardEdgeSystem,
} from "../api/dashboardApi";
import { getTenants } from "../api/tenantApi";
import { getVpnUsers } from "../api/vpnApi";
import { apiClient } from "../api/apiClient";
import { toast } from "react-toastify";

export default function Home() {
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuperAdmin = me?.global_role === "superadmin";
  const [searchParams] = useSearchParams();

  // Superadmin istatistikleri
  const [adminStats, setAdminStats] = useState({
    edgeCount: null,
    tenantCount: null,
    userCount: null,
    vpnUserCount: null,
  });

  useEffect(() => {
    if (!isSuperAdmin || !accessToken) return;

    async function fetchAdminStats() {
      const [edgeRes, tenantRes, vpnRes, userRes] = await Promise.allSettled([
        getEdges(accessToken),
        getTenants(accessToken, { limit: 1 }),
        getVpnUsers(accessToken, { limit: 1 }),
        apiClient.get("/auth/users/list?limit=1", { headers: { Authorization: `Bearer ${accessToken}` } }),
      ]);

      setAdminStats({
        edgeCount:    edgeRes.status   === "fulfilled" ? (edgeRes.value?.total   ?? edgeRes.value?.length   ?? 0) : 0,
        tenantCount:  tenantRes.status === "fulfilled" ? (tenantRes.value?.total ?? tenantRes.value?.length ?? 0) : 0,
        vpnUserCount: vpnRes.status    === "fulfilled" ? (vpnRes.value?.total    ?? vpnRes.value?.length    ?? 0) : 0,
        userCount:    userRes.status   === "fulfilled" ? (userRes.value?.data?.total ?? 0) : 0,
      });
    }

    fetchAdminStats();
  }, [isSuperAdmin, accessToken]);

  const [timeRange, setTimeRange] = useState("15m");
  const [refreshInterval, setRefreshInterval] = useState("5s");
  const [edges, setEdges] = useState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [dashboard, setDashboard] = useState(defaultDashboard);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Edge'den cihaz/tag seçimi (widget eklerken)
  const [dashboardDevices, setDashboardDevices] = useState([]);
  const [deviceTags, setDeviceTags] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [addWidgetDeviceIds, setAddWidgetDeviceIds] = useState([]);
  const [addWidgetTagNames, setAddWidgetTagNames] = useState([]);
  const [addWidgetType, setAddWidgetType] = useState("gauge");
  const [addWidgetRange, setAddWidgetRange] = useState("1h");
  const [devicesError, setDevicesError] = useState(null);

  // Mock sensor data - InfluxDB'den gelecek
  const [sensorData, setSensorData] = useState({
    edgeId: "edge-001",
    temperature: 54.8,
    vibration: 25.5,
    pressure: 104,
    voltage: 223,
    current: 11.8,
    cpu: 0.877,
    ram: 15.2,
    disk: 0.608,
  });

  // Container width hesapla
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Dashboard için erişilebilir edge'leri yükle (tenant admin kendi edge'lerini görür)
  useEffect(() => {
    if (!accessToken) return;

    async function fetchEdges() {
      try {
        const items = await getDashboardEdges(accessToken);
        const list = Array.isArray(items) ? items : [];
        const normalized = list.map((e) => ({
          id: e.id,
          edgeId: e.edge_id ?? e.edgeId,
          name: e.name ?? e.edge_id ?? e.edgeId ?? e.id,
        }));
        setEdges(normalized);
        if (normalized.length > 0) {
          const targetId = searchParams.get("edgeId");
          const target = targetId ? normalized.find((e) => e.id === targetId || e.edgeId === targetId) : null;
          const selected = target || normalized[0];
          setSelectedEdge(selected);
          setSensorData((prev) => ({ ...prev, edgeId: selected.edgeId }));
        }
      } catch (err) {
        console.error("Dashboard edge listesi alınamadı:", err);
        setEdges([]);
      }
    }

    fetchEdges();
  }, [accessToken, searchParams]);

  // InfluxDB'den sensör verilerini çek
  const fetchSensorData = useCallback(async () => {
    if (!accessToken || !selectedEdge) return;

    try {
      const data = await getLatestMetrics(accessToken, selectedEdge.edgeId);
      if (data && !data.error) {
        setSensorData((prev) => ({
          ...prev,
          edgeId: selectedEdge.edgeId,
          temperature: data.temperature ?? prev.temperature,
          vibration: data.vibration ?? prev.vibration,
          pressure: data.pressure ?? prev.pressure,
          voltage: data.voltage ?? prev.voltage,
          current: data.current ?? prev.current,
        }));
      }
    } catch (err) {
      console.error("Sensör verisi alınamadı:", err);
    }
  }, [accessToken, selectedEdge]);

  // Seçilen edge'in cihaz listesi (dashboard widget eklerken)
  useEffect(() => {
    if (!accessToken || !selectedEdge?.edgeId) {
      setDashboardDevices([]);
      setDevicesError(null);
      return;
    }
    setDevicesError(null);
    getDashboardEdgeDevices(accessToken, selectedEdge.edgeId)
      .then((list) => {
        setDashboardDevices(Array.isArray(list) ? list : []);
        setDevicesError(null);
      })
      .catch((err) => {
        setDashboardDevices([]);
        const msg = err.response?.data?.detail || err.message || "Cihaz listesi alınamadı";
        setDevicesError(typeof msg === "string" ? msg : JSON.stringify(msg));
        toast.error(msg);
      });
  }, [accessToken, selectedEdge?.edgeId]);

  // Seçilen cihaz(lar) için tag listesi
  useEffect(() => {
    if (!accessToken || !selectedEdge?.edgeId || addWidgetDeviceIds.length === 0) {
      setDeviceTags([]);
      return;
    }
    const load = async () => {
      const all = [];
      for (const deviceId of addWidgetDeviceIds) {
        try {
          const tags = await getDashboardEdgeDeviceTags(accessToken, selectedEdge.edgeId, deviceId);
          all.push(...(tags || []).map((t) => ({ ...t, deviceId })));
        } catch (_) {}
      }
      const seen = new Set();
      setDeviceTags(all.filter((t) => {
        const k = t.name;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }));
    };
    load();
  }, [accessToken, selectedEdge?.edgeId, addWidgetDeviceIds]);

  // Cihaz/tag seçimli + edge_system widget'lar için veri çek
  const fetchWidgetData = useCallback(async () => {
    const widgets = dashboard?.widgets ?? [];
    const withEdge = widgets.filter(
      (w) => w?.config?.edgeId && w?.config?.deviceIds?.length && w?.config?.tagNames?.length
    );
    const systemWidgets = widgets.filter((w) => w.type === "edge_system" && w?.config?.edgeId);
    const logsWidgets = widgets.filter((w) => w.type === "edge_logs" && w?.config?.edgeId);
    const alarmWidgets = widgets.filter((w) => w.type === "alarm_table" && w?.config?.edgeId);
    const anomalyWidgets = widgets.filter((w) => w.type === "anomaly_table" && w?.config?.edgeId);
    const next = {};
    if (!withEdge.length && !systemWidgets.length && !logsWidgets.length && !alarmWidgets.length && !anomalyWidgets.length) return;
    for (const w of withEdge) {
      try {
        const needsSeries = w.type === "linechart" || w.type === "table";
        const [latest, seriesRes] = await Promise.all([
          getDashboardDataLatest(accessToken, w.config.edgeId, w.config.deviceIds, w.config.tagNames),
          needsSeries
            ? getDashboardDataSeries(
                accessToken,
                w.config.edgeId,
                w.config.deviceIds,
                w.config.tagNames,
                timeRange
              )
            : Promise.resolve(null),
        ]);
        next[w.id] = {
          latest: Array.isArray(latest) ? latest : [],
          series: seriesRes?.data || [],
        };
      } catch (_) {
        next[w.id] = { latest: [], series: [] };
      }
    }
    for (const w of systemWidgets) {
      try {
        const data = await getDashboardEdgeSystem(accessToken, w.config.edgeId);
        next[w.id] = { system: data };
      } catch (_) {
        next[w.id] = { system: null };
      }
    }
    for (const w of logsWidgets) {
      try {
        const data = await getDashboardEdgeSystem(accessToken, w.config.edgeId);
        if (data) {
          const ts = data.ts ?? data.timestamp;
          setWidgetData((prev) => {
            const existing = prev[w.id]?.logs ?? [];
            const alreadyExists = ts && existing.some((e) => (e.ts ?? e.timestamp) === ts);
            if (alreadyExists) return prev;
            const newEntry = { ...data, fetchedAt: new Date().toISOString() };
            return { ...prev, [w.id]: { logs: [...existing, newEntry].slice(-500) } };
          });
        }
      } catch (_) {}
    }
    if (alarmWidgets.length) {
      try {
        // Aynı edgeId'ye sahip tüm alarm_table widget'ları için tek istek yeterli
        const byEdge = new Map();
        for (const w of alarmWidgets) {
          if (!byEdge.has(w.config.edgeId)) byEdge.set(w.config.edgeId, []);
          byEdge.get(w.config.edgeId).push(w);
        }
        for (const [edgeId, ws] of byEdge.entries()) {
          const alarms = await getEdgeAlarms(accessToken, edgeId);
          const rows = Array.isArray(alarms)
            ? alarms.map((a) => ({
                time: a.time ? new Date(a.time).toLocaleString("tr-TR") : "-",
                field: a.field ?? a.metric ?? "-",
                severity: a.severity ?? "-",
                value: a.value ?? "-",
                threshold: a.threshold ?? "-",
                message: a.message ?? "",
              }))
            : [];
          for (const w of ws) {
            next[w.id] = { ...(next[w.id] || {}), alarms: rows };
          }
        }
      } catch (_) {
        for (const w of alarmWidgets) {
          next[w.id] = { ...(next[w.id] || {}), alarms: [] };
        }
      }
    }
    if (anomalyWidgets.length) {
      try {
        const byEdge = new Map();
        for (const w of anomalyWidgets) {
          if (!byEdge.has(w.config.edgeId)) byEdge.set(w.config.edgeId, []);
          byEdge.get(w.config.edgeId).push(w);
        }
        for (const [edgeId, ws] of byEdge.entries()) {
          const anomalies = await getEdgeAnomalies(accessToken, edgeId);
          const rows = Array.isArray(anomalies)
            ? anomalies.map((a) => ({
                time: a.time ? new Date(a.time).toLocaleString("tr-TR") : "-",
                field: a.field ?? "-",
                level: a.level ?? "-",
                value: a.value ?? "-",
                score: a.score ?? "-",
              }))
            : [];
          for (const w of ws) {
            next[w.id] = { ...(next[w.id] || {}), anomalies: rows };
          }
        }
      } catch (_) {
        for (const w of anomalyWidgets) {
          next[w.id] = { ...(next[w.id] || {}), anomalies: [] };
        }
      }
    }
    setWidgetData((prev) => ({ ...prev, ...next }));
  }, [accessToken, dashboard?.widgets, timeRange]);

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  // İlk yükleme ve edge değiştiğinde veri çek
  useEffect(() => {
    fetchSensorData();
  }, [fetchSensorData]);

  // Periyodik veri güncelleme
  useEffect(() => {
    if (refreshInterval === "off") return;

    const intervalMs = {
      "5s": 5000,
      "10s": 10000,
      "30s": 30000,
      "1m": 60000,
    }[refreshInterval] || 5000;

    const interval = setInterval(() => {
      fetchSensorData();
      fetchWidgetData();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [refreshInterval, fetchSensorData, fetchWidgetData]);

  // Layout değişikliğini handle et
  const handleLayoutChange = (newLayout) => {
    const updatedWidgets = dashboard.widgets.map((widget) => {
      const layoutItem = newLayout.find((l) => l.id === widget.id);
      if (layoutItem) {
        return { ...widget, position: layoutItem.position };
      }
      return widget;
    });

    setDashboard((prev) => ({ ...prev, widgets: updatedWidgets }));
  };

  // Dashboard'u backend'e kaydet
  const saveDashboard = async () => {
    try {
      await saveDashboardLayout(accessToken, JSON.stringify(dashboard));
      setEditMode(false);
      toast.success("Dashboard başarıyla kaydedildi!");
    } catch (err) {
      console.error("Dashboard kaydedilemedi:", err);
      toast.error("Dashboard kaydedilemedi!");
    }
  };

  // Dashboard'u backend'den yükle
  useEffect(() => {
    if (!accessToken) return;

    async function fetchLayout() {
      try {
        const data = await getDashboardLayout(accessToken);
        if (data && data.layout_json) {
          setDashboard(JSON.parse(data.layout_json));
        }
      } catch (err) {
        console.error("Dashboard yüklenemedi:", err);
      }
    }

    fetchLayout();
  }, [accessToken]);

  // Superadmin görünümü
  if (isSuperAdmin) {
    const statCards = [
      { label: "Toplam Cihaz",         value: adminStats.edgeCount,    icon: "🖥️",  color: "#58a6ff" },
      { label: "Toplam Tenant",        value: adminStats.tenantCount,  icon: "🏢",  color: "#3fb950" },
      { label: "Toplam Kullanıcı",     value: adminStats.userCount,    icon: "👥",  color: "#d2a8ff" },
      { label: "VPN Kullanıcısı",      value: adminStats.vpnUserCount, icon: "🔐",  color: "#ffa657" },
    ];

    return (
      <div style={styles.container}>
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ color: "#e6edf3", margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Genel Bakış
          </h2>
          <p style={{ color: "#8b949e", margin: "4px 0 0", fontSize: "13px" }}>
            Sistemdeki güncel istatistikler
          </p>
        </div>

        <div style={styles.statsGrid}>
          {statCards.map((card) => (
            <div key={card.label} style={styles.statCard}>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>{card.icon}</div>
              <div style={{ fontSize: "36px", fontWeight: 700, color: card.color, lineHeight: 1 }}>
                {card.value === null ? "..." : card.value}
              </div>
              <div style={{ fontSize: "13px", color: "#8b949e", marginTop: "8px" }}>
                {card.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.breadcrumb}>
          <span>Home</span>
          <span style={styles.separator}>›</span>
          <span>Dashboards</span>
          <span style={styles.separator}>›</span>
          <span style={styles.active}>Dashboard · Edge cihaz/tag ile widget ekle</span>
        </div>
        <div style={styles.controls}>
          {/* Edge Selector */}
          {edges.length > 0 && (
            <select
              style={styles.select}
              value={selectedEdge?.id || ""}
              onChange={(e) => {
                const edge = edges.find((ed) => ed.id === e.target.value);
                setSelectedEdge(edge);
                if (edge) {
                  setSensorData((prev) => ({ ...prev, edgeId: edge.edgeId }));
                }
              }}
            >
              {edges.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.edgeId}
                </option>
              ))}
            </select>
          )}

          <select
            style={styles.select}
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="5m">Last 5 minutes</option>
            <option value="15m">Last 15 minutes</option>
            <option value="30m">Last 30 minutes</option>
            <option value="1h">Last 1 hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
          </select>

          <button style={styles.refreshBtn} onClick={fetchSensorData}>↻ Refresh</button>

          <select
            style={styles.select}
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(e.target.value)}
          >
            <option value="off">Off</option>
            <option value="5s">5s</option>
            <option value="10s">10s</option>
            <option value="30s">30s</option>
            <option value="1m">1m</option>
          </select>

          <div style={styles.divider} />

          {editMode ? (
            <>
              <button style={styles.saveBtn} onClick={saveDashboard}>
                Kaydet
              </button>
              <button
                style={styles.resetBtn}
                onClick={() => setShowResetModal(true)}
              >
                Varsayilana Dön
              </button>
              <button
                style={styles.cancelBtn}
                onClick={() => setEditMode(false)}
              >
                İptal
              </button>
            </>
          ) : (
            <button
              style={styles.editBtn}
              onClick={() => setEditMode(true)}
            >
              Düzenle
            </button>
          )}
        </div>
      </div>

      {/* Widget ekle paneli: Düzenle modunda VEYA widget yokken her zaman göster (tenant admin görsün) */}
      {(editMode || !dashboard?.widgets?.length) && (
        <div style={styles.addPanel}>
          <span style={styles.addPanelTitle}>Yeni widget ekle:</span>
          {edges.length === 0 ? (
            <span style={{ color: "#8b949e", fontSize: "14px" }}>
              Size atanmış edge yok. Dashboard'a cihaz eklemek için yöneticiyle iletişime geçin.
            </span>
          ) : (
            <>
              <label style={styles.addPanelLabel}>Edge</label>
              <select
                style={styles.select}
                value={selectedEdge?.id ?? ""}
                onChange={(e) => {
                  const edge = edges.find((x) => x.id === e.target.value || x.edgeId === e.target.value);
                  setSelectedEdge(edge ?? null);
                  if (edge) setSensorData((prev) => ({ ...prev, edgeId: edge.edgeId }));
                }}
              >
                <option value="">Edge seçin</option>
                {edges.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.edgeId ?? e.name}
                  </option>
                ))}
              </select>
              <label style={styles.addPanelLabel}>Cihaz(lar)</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <select
                  style={styles.select}
                  multiple
                  value={addWidgetDeviceIds}
                  onChange={(e) => setAddWidgetDeviceIds(Array.from(e.target.selectedOptions, (o) => o.value))}
                  disabled={!selectedEdge}
                  title={!selectedEdge ? "Önce edge seçin" : ""}
                >
                  {dashboardDevices.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {devicesError && (
                  <span style={{ fontSize: "12px", color: "#f85149" }}>{devicesError}</span>
                )}
              </div>
              <label style={styles.addPanelLabel}>Tag(ler)</label>
              <select
                style={styles.select}
                multiple
                value={addWidgetTagNames}
                onChange={(e) => setAddWidgetTagNames(Array.from(e.target.selectedOptions, (o) => o.value))}
                disabled={!selectedEdge || addWidgetDeviceIds.length === 0}
                title={!selectedEdge ? "Önce edge seçin" : addWidgetDeviceIds.length === 0 ? "Önce cihaz seçin" : ""}
              >
                {deviceTags.map((t) => (
                  <option key={t.id} value={t.name}>{t.label || t.name} {t.unit ? `(${t.unit})` : ""}</option>
                ))}
              </select>
              <label style={styles.addPanelLabel}>Widget tipi</label>
              <select style={styles.select} value={addWidgetType} onChange={(e) => setAddWidgetType(e.target.value)}>
                <option value="edge_system">Edge sistemi (CPU / RAM / Disk) — sadece edge seçin</option>
                <option value="edge_logs">Edge Logs (host_system geçmişi) — sadece edge seçin</option>
                <option value="alarm_table">Alarm tablosu</option>
                <option value="anomaly_table">Anomali tablosu</option>
                <option value="gauge">Gauge</option>
                <option value="linechart">Grafik</option>
                <option value="table">Tablo</option>
                <option value="statuscard">Status</option>
              </select>
              {(addWidgetType === "linechart" || addWidgetType === "table") && (
                <select style={styles.select} value={addWidgetRange} onChange={(e) => setAddWidgetRange(e.target.value)}>
                  <option value="15m">15 dk</option>
                  <option value="1h">1 saat</option>
                  <option value="24h">24 saat</option>
                </select>
              )}
              <button
                style={styles.saveBtn}
                onClick={() => {
                  if (!selectedEdge) {
                    toast.warning("Önce bir edge seçin.");
                    return;
                  }
                  const isEdgeSystem = addWidgetType === "edge_system";
                  const isEdgeLogs = addWidgetType === "edge_logs";
                  const isAlarmTable = addWidgetType === "alarm_table";
                  const isAnomalyTable = addWidgetType === "anomaly_table";
                  const needsDeviceTags = !isEdgeSystem && !isEdgeLogs && !isAlarmTable && !isAnomalyTable;
                  if (needsDeviceTags && (!addWidgetDeviceIds.length || !addWidgetTagNames.length)) {
                    toast.warning("Lütfen en az bir cihaz ve bir tag seçin.");
                    return;
                  }
                  const id = `w-${Date.now()}`;
                  const widgets = dashboard?.widgets ?? [];
                  const deviceNames = dashboardDevices.filter((d) => addWidgetDeviceIds.includes(d.id)).map((d) => d.name).join(", ");
                  const tagLabels = deviceTags.filter((t) => addWidgetTagNames.includes(t.name)).map((t) => t.label || t.name).join(", ");
                  const newWidget =
                    isEdgeSystem
                      ? {
                          id,
                          type: "edge_system",
                          position: { x: (widgets.length % 4) * 3, y: Math.floor(widgets.length / 4) * 3, w: 4, h: 2 },
                          config: {
                            edgeId: selectedEdge.edgeId,
                            title: `${selectedEdge.edgeId ?? selectedEdge.name ?? "Edge"} · Sistem`,
                          },
                        }
                      : isEdgeLogs
                      ? {
                          id,
                          type: "edge_logs",
                          position: { x: (widgets.length % 4) * 3, y: Math.floor(widgets.length / 4) * 3, w: 12, h: 5 },
                          config: {
                            edgeId: selectedEdge.edgeId,
                            title: `${selectedEdge.edgeId ?? selectedEdge.name ?? "Edge"} · Edge Logs`,
                            range: "1h",
                          },
                        }
                      : isAlarmTable
                      ? {
                          id,
                          type: "alarm_table",
                          position: { x: (widgets.length % 4) * 3, y: Math.floor(widgets.length / 4) * 3, w: 5, h: 4 },
                          config: {
                            edgeId: selectedEdge.edgeId,
                            title: `${selectedEdge.edgeId ?? selectedEdge.name ?? "Edge"} · Alarmlar`,
                          },
                        }
                      : isAnomalyTable
                      ? {
                          id,
                          type: "anomaly_table",
                          position: { x: (widgets.length % 4) * 3, y: Math.floor(widgets.length / 4) * 3, w: 5, h: 4 },
                          config: {
                            edgeId: selectedEdge.edgeId,
                            title: `${selectedEdge.edgeId ?? selectedEdge.name ?? "Edge"} · Anomaliler`,
                          },
                        }
                      : {
                          id,
                          type: addWidgetType,
                          position: { x: (widgets.length % 4) * 3, y: Math.floor(widgets.length / 4) * 3, w: 3, h: 3 },
                          config: {
                            edgeId: selectedEdge.edgeId,
                            deviceIds: [...addWidgetDeviceIds],
                            tagNames: [...addWidgetTagNames],
                            title: `${deviceNames} · ${tagLabels}`,
                            range: (addWidgetType === "linechart" || addWidgetType === "table") ? addWidgetRange : undefined,
                          },
                        };
                  setDashboard((prev) => ({ ...prev, widgets: [...(prev?.widgets ?? []), newWidget] }));
                  if (!isEdgeSystem && !isEdgeLogs && !isAlarmTable && !isAnomalyTable) {
                    setAddWidgetDeviceIds([]);
                    setAddWidgetTagNames([]);
                  }
                  toast.success("Widget eklendi.");
                }}
              >
                Widget ekle
              </button>
            </>
          )}
        </div>
      )}

      {editMode && (
        <div style={styles.editInfo}>
          <span>
            Düzenleme modu: Widget'ları sürükleyerek taşıyabilir, ✕ ile silebilirsiniz. Yeni widget için edge seçin, cihaz ve tag seçip &quot;Widget ekle&quot;ye tıklayın.
          </span>
        </div>
      )}

      {/* Edge yoksa bilgi */}
      {!isSuperAdmin && edges.length === 0 && accessToken && (
        <div style={styles.editInfo}>
          <span style={{ color: "#8b949e" }}>Dashboard için size atanmış edge yok. Cihaz ekleyebilmek için yöneticiyle iletişime geçin.</span>
        </div>
      )}

      {showResetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Varsayılana Dön</h3>
            <p style={styles.modalText}>Dashboard varsayılan düzene dönecek. Emin misiniz?</p>
            <div style={styles.modalButtons}>
              <button
                style={styles.modalBtnYes}
                onClick={() => {
                  setDashboard(defaultDashboard);
                  setShowResetModal(false);
                }}
              >
                Evet
              </button>
              <button style={styles.modalBtnNo} onClick={() => setShowResetModal(false)}>
                Hayır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boş dashboard mesajı (edge ile aynı) veya grid */}
      {!dashboard?.widgets?.length ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>Henüz widget yok.</p>
          <p style={styles.emptyHint}>
            Düzenle&apos;ye tıklayıp edge seçin, cihaz ve tag seçerek widget ekleyin. Eklediğiniz widget&apos;ları sürükleyebilir veya ✕ ile silebilirsiniz.
          </p>
        </div>
      ) : (
        <DashboardGrid
          widgets={dashboard.widgets}
          onLayoutChange={handleLayoutChange}
          onRemoveWidget={(id) =>
            setDashboard((prev) => ({
              ...prev,
              widgets: (prev?.widgets ?? []).filter((w) => w.id !== id),
            }))
          }
          sensorData={sensorData}
          widgetData={widgetData}
          editable={editMode}
          width={containerWidth - 48}
          cols={12}
          rowHeight={80}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    color: "#e6edf3",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginTop: "8px",
  },
  statCard: {
    backgroundColor: "#161b22",
    border: "1px solid #30363d",
    borderRadius: "12px",
    padding: "28px 24px",
    textAlign: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "8px",
  },
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#8b949e",
    fontSize: "14px",
  },
  separator: {
    color: "#484f58",
  },
  active: {
    color: "#e6edf3",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  select: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "5px 10px",
    fontSize: "12px",
    cursor: "pointer",
  },
  refreshBtn: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  divider: {
    width: "1px",
    height: "24px",
    backgroundColor: "#30363d",
    margin: "0 8px",
  },
  editBtn: {
    backgroundColor: "#238636",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  saveBtn: {
    backgroundColor: "#238636",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  resetBtn: {
    backgroundColor: "#da3633",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  cancelBtn: {
    backgroundColor: "#21262d",
    color: "#e6edf3",
    border: "1px solid #30363d",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    minWidth: "340px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
  },
  modalTitle: {
    margin: "0 0 12px 0",
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a1a2e",
  },
  modalText: {
    margin: "0 0 24px 0",
    fontSize: "14px",
    color: "#555",
  },
  modalButtons: {
    display: "flex",
    gap: "12px",
  },
  modalBtnYes: {
    padding: "8px 24px",
    backgroundColor: "#da3633",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  modalBtnNo: {
    padding: "8px 24px",
    backgroundColor: "#e0e0e0",
    color: "#333",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  editInfo: {
    backgroundColor: "#1f6feb22",
    border: "1px solid #1f6feb",
    borderRadius: "6px",
    padding: "12px 16px",
    marginBottom: "16px",
    color: "#58a6ff",
    fontSize: "13px",
  },
  addPanel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    marginBottom: "12px",
    padding: "12px",
    backgroundColor: "#21262d",
    borderRadius: "8px",
    border: "1px solid #30363d",
  },
  addPanelTitle: {
    color: "#8b949e",
    fontSize: "13px",
  },
  addPanelLabel: {
    color: "#8b949e",
    fontSize: "12px",
    marginLeft: "8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 24px",
    backgroundColor: "#161b22",
    borderRadius: "8px",
    border: "1px dashed #30363d",
  },
  emptyText: {
    color: "#e6edf3",
    fontSize: "16px",
    margin: "0 0 8px 0",
  },
  emptyHint: {
    color: "#8b949e",
    fontSize: "14px",
    margin: 0,
  },
};
